import { formatDate } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import flatpickr from 'flatpickr';
import { ExportFilePopupComponent } from 'src/app/components/export-file-popup/export-file-popup.component';
import { CompanyService } from 'src/app/services/company.service';
import { CustomerService } from 'src/app/services/customer.service';
import { PdfService } from 'src/app/services/pdf.services';
import { SortColumn, SortDirection } from 'src/app/services/sortable.directive';
import { StatesService } from 'src/app/services/states.service';
import { getPreviousMonthRange } from 'src/app/variables/common';
import { compare } from 'src/app/variables/constant';

export type ISearchReportReq = {
  company?: string;
  status?: number | null;
  state?: string;
  user?: string;
  expiresIn?: number | null;
  fileName?: string;
  format?: number;
  customerNumber?: string;
  lastEditedBy?: string;
  priorityOnly?: boolean;
  submittedFromDate?: string | Date | null;
  submittedToDate?: string | Date | null;
  lastEditFromDate?: string | Date | null;
  lastEditToDate?: string | Date | null;
  fileNameList?: Array<string>;
};

@Component({
  selector: 'app-valid-invalid-reports',
  templateUrl: './valid-invalid-reports.component.html',
  styleUrls: ['./valid-invalid-reports.component.scss']
})
export class ValidInvalidReportsComponent implements OnInit, AfterViewInit {

  @ViewChild('selectAll', { static: false }) selectAll!: ElementRef;

  searchResultList: any[];
  isLoading: boolean = false;
  collectionSize: number = 0;
  page: number = 1;
  pageSize: number = 10;
  currentColumn: SortColumn = "";
  currentSortBy: SortDirection = "desc";
  companyCode = localStorage.getItem('company_code');
  selectedIds: any[] = [];
  sortedField;
  isDescending = true;
  filterForm: FormGroup;
  statusList = [
    { name: "All", value: "All" },
    { name: "Valid", value: "Valid" },
    { name: "Invalid", value: "Invalid" }
  ];
  expireInList = [
    { name: "All", value: 0 },
    { name: "6 Months", value: 1 },
    { name: "3 Months", value: 2 },
    { name: "1 Month", value: 3 },
    { name: "Expired", value: 4 }
  ];
  stateList = [];
  submittedPicker;
  lastEditPicker;
  isRefreshChart!: boolean;
  filterReq: ISearchReportReq = {
    company: localStorage.getItem('company_code'),
    submittedFromDate: null,
    submittedToDate: null,
    lastEditFromDate: null,
    lastEditToDate: null
  } as ISearchReportReq;
  pdfFile: SafeResourceUrl;

  constructor(
    private customerService: CustomerService,
    private activeModal: NgbModal,
    private formBuilder: FormBuilder,
    private statesService: StatesService,
    private companyService: CompanyService,
    private pdfService: PdfService,
    public sanitizer: DomSanitizer,
    @Inject(LOCALE_ID) public locale: string,
  ) {
    this.getStates();
  }

  ngOnInit(): void {
    const { start, end } = getPreviousMonthRange(0);
    this.filterForm = this.formBuilder.group({
      status: ['All'],
      state: [""],
      expiresIn: [0],
      customerNumber: [""],
      fileName: [""],
      submitted_date_Range: [formatDate(start, "MM/dd/yyyy", this.locale) + ' to ' + formatDate(end, "MM/dd/yyyy", this.locale)],
      submittedFromDate: [start],
      submittedToDate: [end],
      last_edit_date_Range: [""],
      lastEditFromDate: [null],
      lastEditToDate: [null],
      priorityOnly: [false],
    });
    this.filterReq = { company: localStorage.getItem('company_code'), ...this.filterForm.value };
    this.loadData();
  }

  ngAfterViewInit() {
    // Submitted Date Range Picker
    this.submittedPicker = flatpickr('#submittedDateRange', {
      mode: 'range',
      dateFormat: 'm/d/Y',
      onChange: (selectedDates, dateStr) => {
        if (selectedDates.length === 2) {
          const [start, end] = selectedDates;
          dateStr = dateStr.replace(' to ', ' – ');
          this.filterForm.patchValue({
            submitted_date_Range: dateStr,
            submittedFromDate: start ? start.toISOString() : '',
            submittedToDate: end ? end.toISOString() : '',
          });
        } else if (selectedDates.length === 0) {
          this.filterForm.patchValue({
            submitted_date_Range: '',
            submittedFromDate: null,
            submittedToDate: null,
          });
        }
      }
    });

    // Last Edit Date Range Picker
    this.lastEditPicker = flatpickr('#lastEditDateRange', {
      mode: 'range',
      dateFormat: 'm/d/Y',
      onChange: (selectedDates, dateStr) => {
        if (selectedDates.length === 2) {
          const [start, end] = selectedDates;
          dateStr = dateStr.replace(' to ', ' – ');
          this.filterForm.patchValue({
            last_edit_date_Range: dateStr,
            lastEditFromDate: start ? start.toISOString() : '',
            lastEditToDate: end ? end.toISOString() : '',
          });
        } else if (selectedDates.length === 0) {
          this.filterForm.patchValue({
            last_edit_date_Range: '',
            lastEditFromDate: null,
            lastEditToDate: null,
          });
        }
      }
    });
  }

  /**
   * Fetches all states data from server and populates the stateList variable
   */
  getStates() {
    this.statesService
      .getAllStatesData()
      .subscribe((res: any) => {
        if (res && res['result']) {
          this.stateList = res['result'];
        }
      })
  }

  /**
   * Fetches customer update requests from server with given pagination parameters.
   * Sets isLoading to true before the request, and false after the request is finished.
   * Sets searchResultList and collectionSize based on the response from the server.
   * If the request fails, sets searchResultList and collectionSize to empty array and 0 respectively.
   */
  loadData() {
    this.isLoading = true;
    let payload = {
      page: this.page || 1,
      size: this.pageSize || 10,
      ...this.filterReq,
      // fileNameList: []
    }
    this.isRefreshChart = !this.isRefreshChart;
    this.companyService
      .getValidInvalidCertificatesDetails(payload)
      .subscribe((res: any) => {
        console.log("resresres", res);

        this.isLoading = false;
        if (res && res['result']) {
          this.searchResultList = res['result'];
          this.collectionSize = res?.totalRecords || res?.length;
        } else {
          this.searchResultList = [];
          this.collectionSize = 0;
        }
      }, () => {
        this.isLoading = false;
        this.searchResultList = [];
        this.collectionSize = 0;
      })
  }

  /**
   * Opens a modal with the given file's PDF content.
   * Calls getPdfMaster from the PdfService to get the PDF file's content.
   * If the response is not empty, sets the pdfFile variable to the response's content.
   * Opens the given modal with the pdfFile's content.
   * @param file The file to open in the modal
   * @param modal The modal to open
   */
  onViewPDF(file, modal) {
    this.pdfService.getPdfMaster(file).subscribe((res) => {
      if (res != "" || res != undefined || res != null) {
        this.pdfFile = this.sanitizer.bypassSecurityTrustResourceUrl(res['result']);
        this.activeModal.open(modal, { centered: true, size: 'lg', scrollable: true, ariaLabelledBy: 'pdfViewerModalTitle' });
      }
    })
  }

  /**
   * Resets the search form and reloads the data from the server
   */
  onReset() {
    const { start, end } = getPreviousMonthRange(0);
    this.filterForm.reset();
    this.submittedPicker.clear();
    this.lastEditPicker.clear();
    this.filterForm.get('status').setValue("All");
    this.filterForm.get('expiresIn').setValue(0);
    this.filterForm.get('priorityOnly').setValue(false);
    this.filterForm.get('submittedFromDate').setValue(start);
    this.filterForm.get('submittedToDate').setValue(end);
    this.filterForm.get('submitted_date_Range').setValue(start + ' to ' + end);
    this.page = 1;
    this.filterReq = { company: localStorage.getItem('company_code'), ...this.filterForm.value };
    this.loadData();
  }

  /**
   * Submits the search form and logs the constructed payload to the console.
   */
  onSearch() {
    this.filterReq = {
      submittedFromDate: this.filterForm.get('submittedFromDate').value,
      submittedToDate: this.filterForm.get('submittedToDate').value,
      lastEditFromDate: this.filterForm.get('lastEditFromDate').value,
      lastEditToDate: this.filterForm.get('lastEditToDate').value,
      company: localStorage.getItem('company_code'),
      state: this.filterForm.get('state').value,
      status: this.filterForm.get('status').value,
      expiresIn: this.filterForm.get('expiresIn').value,
      fileName: this.filterForm.get('fileName').value,
      customerNumber: this.filterForm.get('customerNumber').value,
      priorityOnly: this.filterForm.get('priorityOnly').value
    };
    this.page = 0;
    this.loadData();
  }


  onExport() {
    const modalRef = this.activeModal.open(ExportFilePopupComponent, { centered: true, size: 'lg', ariaLabelledBy: 'exportFilePopupTitle' });
    modalRef.componentInstance.filterReq = this.filterReq;
    modalRef.componentInstance.selectedFiles = this.selectedIds;

    modalRef.result.then(
      (data: any) => {
        this.selectedIds = [];
        this.selectAll.nativeElement.checked = false;
      },
      (reason: any) => {
        console.log(reason);
      }
    );
  }

  // Handle single checkbox change
  onCheckboxChange(event: any, file: string) {
    if (event.target.checked) {
      this.selectedIds.push(file);
    } else {
      this.selectedIds = this.selectedIds.filter(i => i !== file);
    }

    if (this.selectedIds.length == 0) {
      this.selectAll.nativeElement.checked = false;
    } else {
      this.selectAll.nativeElement.checked = true;
    }
    this.filterReq.fileNameList = this.selectedIds;
  }

  // Handle "Select All" functionality
  selectAllCheckbox(event: any) {
    if (event.target.checked) {
      this.selectedIds = this.searchResultList.map(item => item.fileName);
    } else {
      this.selectedIds = [];
    }
    this.filterReq.fileNameList = this.selectedIds;
  }

  // Check if the checkbox is selected
  isChecked(id: string) {
    return this.selectedIds.includes(id);
  }

  changePage(e) {
    this.page = e;
    this.loadData();
  }

  resetCustomerPage() {
    this.page = 1;
    this.loadData();
  }

  closeModal() {
    this.activeModal.dismissAll();
  }

  /**
   * Sorts the search results based on the given column and direction.
   * When the column is first clicked, it sorts in ascending order.
   * When the column is clicked again, it sorts in descending order.
   * @param event The event object that triggered this function.
   */
  onSort(event) {
    var column = event.target.getAttribute("sortable");
    this.sortedField = column;
    if (this.currentColumn !== column && this.currentColumn !== "") {
      this.currentColumn = column;
      this.currentSortBy = "asc";
    } else {
      if (this.currentColumn == "") {
        this.currentColumn = column;
        if (this.currentColumn == "customerName") {
          this.currentSortBy = "desc";
        }
      } else {
        if (this.currentSortBy == "asc") {
          this.currentSortBy = "desc";
        } else {
          this.currentSortBy = "asc";
        }
      }
    }
    this.isDescending = this.currentSortBy == "desc" ? true : false;
    this.page = 1;
    this.loadData();
  }

  /**
   * Sorts the given datalist based on the given column and direction.
   * If the direction is empty or the column is empty, returns the original datalist.
   * Otherwise, returns a new array sorted based on the given column and direction.
   * @param datalist The array of data to be sorted.
   * @param column The column to sort by.
   * @param direction The direction to sort in, either 'asc' or 'desc'.
   * @returns A new array sorted based on the given column and direction.
  */
  sortData(datalist: any[], column: SortColumn, direction: SortDirection): any[] {
    if (direction === '' || column === '') {
      return datalist;
    } else {
      return [...datalist].sort((a, b) => {
        const res = compare(a[column], b[column]);
        return direction === 'asc' ? res : -res;
      });
    }
  }
}

